/**
 * Loans settings: the lending policy per country, and who may use loans.
 *
 * The policy screen ships no defaults. Each figure on it, the early settlement
 * terms included, is something the owner declares about the business's own
 * jurisdiction; CenoX never fills in a legal limit and never verifies the
 * declaration, and the screen says so. Without a declared policy, only
 * interest-free, fee-free advances to the business's own riders and staff can
 * be issued.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import {
  LOAN_EARLY_REPAYMENT_LABELS,
  LOAN_EARLY_REPAYMENT_POLICIES,
  MEMBER_ROLE_LABELS,
  type LendingPolicy,
  type LoanEarlyRepaymentPolicy,
} from '@cenox/shared';
import { useAuth } from '../../lib/auth';
import { dateTime } from '../../lib/format';
import { Badge, Button, Card, CardHeader, ErrorState, Input, Select, Spinner, Textarea, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { useLendingPolicies, useLoanAccess, useSaveLendingPolicy, useSetLoanAccess, type LoanAccessRow } from './data';

const PERMISSION_LABELS: Array<{ key: string; label: string; hint: string }> = [
  { key: 'loans:read', label: 'View', hint: 'See loans, schedules and reports' },
  { key: 'loans:create', label: 'Create', hint: 'Issue loans and save drafts' },
  { key: 'loans:repay', label: 'Record payments', hint: 'Repayments, settlements and deductions' },
  { key: 'loans:manage', label: 'Correct', hint: 'Edit, cancel, reverse, fees, agreements' },
  { key: 'loans:write_off', label: 'Write off', hint: 'Forgive what is owed' },
  { key: 'loans:configure', label: 'Lending policy', hint: 'Change this page' },
];

export function LoanSettingsPage(): ReactNode {
  const { business, can } = useAuth();
  const isOwner = business?.role === 'owner';
  return (
    <div className="space-y-5">
      <Link to="/loans" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="size-4" aria-hidden /> Loans
      </Link>
      <PageHeader title="Loans settings" description="The lending policy for each country, and who may use loans" />
      <PolicySection canEdit={can('loans:configure')} />
      {isOwner && <AccessSection />}
    </div>
  );
}

function PolicySection({ canEdit }: { canEdit: boolean }): ReactNode {
  const policies = useLendingPolicies();
  const [country, setCountry] = useState<string>('');
  const countries = policies.data?.countries ?? [];
  useEffect(() => {
    if (!country && countries[0]) setCountry(countries[0]);
  }, [country, countries]);

  if (policies.isError) return <ErrorState message="Could not load the lending policy." onRetry={() => void policies.refetch()} />;
  if (!policies.data) return <Spinner label="Loading lending policy" />;

  const policy = policies.data.policies.find((p) => p.countryCode === country) ?? null;
  return (
    <Card>
      <CardHeader
        title="Lending policy"
        subtitle="Record what applies to your business in each country it lends in. CenoX sets no legal limits and does not check what you record here."
        action={
          countries.length > 1 ? (
            <Select
              aria-label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              options={countries.map((c) => ({ value: c, label: c }))}
            />
          ) : undefined
        }
      />
      {country && <PolicyForm key={country} countryCode={country} policy={policy} canEdit={canEdit} />}
    </Card>
  );
}

function PolicyForm({ countryCode, policy, canEdit }: { countryCode: string; policy: LendingPolicy | null; canEdit: boolean }): ReactNode {
  const save = useSaveLendingPolicy();
  const [lendingEnabled, setLendingEnabled] = useState(policy?.lendingEnabled ?? false);
  const [internal, setInternal] = useState(policy?.internalAdvancesAllowed ?? true);
  const [maxRate, setMaxRate] = useState(policy?.maxAnnualRatePercent ?? '');
  const [staffInterest, setStaffInterest] = useState(policy?.staffInterestAllowed ?? false);
  const [fees, setFees] = useState(policy?.feesAllowed ?? false);
  const [licence, setLicence] = useState(policy?.licenceReference ?? '');
  const [notes, setNotes] = useState(policy?.complianceNotes ?? '');
  // A new policy starts with no terms chosen: which the law permits is the owner's to say.
  const [terms, setTerms] = useState<LoanEarlyRepaymentPolicy[]>(policy?.earlySettlementOptions ?? []);
  const [defaultTerm, setDefaultTerm] = useState<LoanEarlyRepaymentPolicy | null>(policy?.defaultEarlySettlement ?? null);
  const [confirm, setConfirm] = useState(false);
  const chosenDefault = defaultTerm && terms.includes(defaultTerm) ? defaultTerm : terms[0] ?? null;
  const ready = !lendingEnabled || (confirm && terms.length > 0);

  const status = !policy
    ? { tone: 'neutral' as const, text: 'No policy: interest-free advances to own riders and staff only' }
    : policy.lendingEnabled
      ? { tone: 'success' as const, text: `Lending on. Declared by ${policy.confirmedByName ?? 'the owner'}${policy.confirmedAt ? ` on ${dateTime(policy.confirmedAt, 'UTC')}` : ''}; not verified by CenoX` }
      : { tone: 'warning' as const, text: 'Lending off: interest-free advances to own riders and staff only' };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          {
            countryCode,
            lendingEnabled,
            internalAdvancesAllowed: internal,
            maxAnnualRatePercent: maxRate.trim() ? maxRate.trim() : null,
            staffInterestAllowed: staffInterest,
            feesAllowed: fees,
            licenceReference: licence.trim() || null,
            complianceNotes: notes.trim() || null,
            ...(terms.length > 0
              ? { earlySettlementOptions: terms, defaultEarlySettlement: chosenDefault ?? undefined }
              : {}),
            confirmCompliance: confirm,
          },
          { onSuccess: () => setConfirm(false) },
        );
      }}
    >
      <Badge tone={status.tone}>{status.text}</Badge>
      <fieldset disabled={!canEdit} className="space-y-3">
        <Toggle checked={internal} onChange={setInternal} label="Allow interest-free advances to own riders and staff" hint="Salary and fuel advances. On unless your country restricts them." />
        <Toggle checked={lendingEnabled} onChange={setLendingEnabled} label="Allow lending with interest or fees, or to people outside the business" />
        {lendingEnabled && (
          <div className="grid grid-cols-1 gap-3 rounded-lg bg-slate-50 p-3 sm:grid-cols-2">
            <Input label="Highest yearly interest rate you may charge (%)" inputMode="decimal" value={maxRate} onChange={(e) => setMaxRate(e.target.value)} hint="Leave empty if none applies to you. Not a CenoX default." />
            <Input label="Licence or registration" value={licence} onChange={(e) => setLicence(e.target.value)} />
            <div className="sm:col-span-2 space-y-2">
              <Toggle checked={staffInterest} onChange={setStaffInterest} label="Interest may be charged to own staff" />
              <Toggle checked={fees} onChange={setFees} label="Processing and late fees may be charged" />
            </div>
            <fieldset className="sm:col-span-2 space-y-2">
              <legend className="text-sm font-medium text-slate-800">Early settlement terms you may offer</legend>
              <p className="text-xs text-slate-500">
                Tick each one the law permits for this business here. Loans with interest can only use these. A loan
                keeps the terms it was issued under if you change this later.
              </p>
              {LOAN_EARLY_REPAYMENT_POLICIES.map((p) => (
                <Toggle
                  key={p}
                  checked={terms.includes(p)}
                  onChange={(on) => setTerms((t) => (on ? LOAN_EARLY_REPAYMENT_POLICIES.filter((x) => x === p || t.includes(x)) : t.filter((x) => x !== p)))}
                  label={LOAN_EARLY_REPAYMENT_LABELS[p]}
                />
              ))}
              {terms.length > 1 && (
                <Select
                  label="Preselected on a new loan"
                  value={chosenDefault ?? ''}
                  onChange={(e) => setDefaultTerm(e.target.value as LoanEarlyRepaymentPolicy)}
                  options={terms.map((p) => ({ value: p, label: LOAN_EARLY_REPAYMENT_LABELS[p] }))}
                />
              )}
              {terms.length === 0 && <p className="text-xs text-amber-700">Choose at least one to switch lending on.</p>}
            </fieldset>
            <div className="sm:col-span-2">
              <Textarea label="Compliance notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
        )}
        {lendingEnabled && (
          <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <input type="checkbox" className="mt-0.5" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
            <span>
              I declare that this policy, including the early settlement terms, reflects the lending rules that apply
              to this business in {countryCode}. This is the business's own statement: CenoX does not verify it, it
              is not legal advice or approval, and CenoX records only who made it and when.
            </span>
          </label>
        )}
      </fieldset>
      {save.error && <p role="alert" className="text-sm text-red-700">{save.error.message}</p>}
      {canEdit ? (
        <Button type="submit" icon={<ShieldCheck className="size-4" />} loading={save.isPending} disabled={!ready}>
          Save policy for {countryCode}
        </Button>
      ) : (
        <p className="text-sm text-slate-500">Only the owner can change the lending policy.</p>
      )}
    </form>
  );
}

function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }): ReactNode {
  return (
    <label className="flex items-start gap-2 text-sm text-slate-800">
      <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      </span>
    </label>
  );
}

/** The owner's grid of who may do what with loans. Only the loan keys change here. */
function AccessSection(): ReactNode {
  const access = useLoanAccess(true);
  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader
          title="Who can use loans"
          subtitle="Grant or remove loan permissions for each person. Other permissions are left as they are. Location logins can never hold these."
        />
      </div>
      {access.isError ? (
        <ErrorState message="Could not load the team." onRetry={() => void access.refetch()} />
      ) : !access.data ? (
        <Spinner label="Loading the team" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" aria-label="Loan access">
            <thead className="border-y border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Person</th>
                {PERMISSION_LABELS.map((p) => (
                  <th key={p.key} className="px-2 py-2 text-center font-medium" title={p.hint}>{p.label}</th>
                ))}
                <th className="px-4 py-2"><span className="sr-only">Save</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {access.data.map((row) => <AccessRow key={row.memberId} row={row} />)}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function AccessRow({ row }: { row: LoanAccessRow }): ReactNode {
  const set = useSetLoanAccess();
  const [wanted, setWanted] = useState<string[]>(row.permissions);
  useEffect(() => setWanted(row.permissions), [row.permissions]);
  const changed = wanted.slice().sort().join() !== row.permissions.slice().sort().join();
  return (
    <tr className={cn(!row.editable && 'text-slate-400')}>
      <td className="px-4 py-2">
        <p className="font-medium text-slate-900">{row.name}</p>
        <p className="text-xs text-slate-500">{MEMBER_ROLE_LABELS[row.role]}</p>
      </td>
      {PERMISSION_LABELS.map((p) => (
        <td key={p.key} className="px-2 py-2 text-center">
          <input
            type="checkbox"
            aria-label={`${p.label} for ${row.name}`}
            checked={wanted.includes(p.key)}
            disabled={!row.editable || set.isPending}
            onChange={(e) =>
              setWanted((w) => (e.target.checked ? [...w, p.key] : w.filter((k) => k !== p.key)))
            }
          />
        </td>
      ))}
      <td className="px-4 py-2 text-right">
        {row.editable && (
          <Button size="sm" variant="secondary" disabled={!changed} loading={set.isPending} onClick={() => set.mutate({ memberId: row.memberId, permissions: wanted })}>
            Save
          </Button>
        )}
        {set.error && <p role="alert" className="mt-1 text-xs text-red-700">{set.error.message}</p>}
      </td>
    </tr>
  );
}
